"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { EASE, spring } from "@/lib/motion";
import type { MobileTab } from "@/lib/content/types";
import { Icon } from "@/components/ui/Icon";
import { Avatar } from "@/components/ui/primitives";
import { useModalChrome } from "@/components/ui/Modal";
import { useContent } from "./ContentContext";
import { RewardsPanel } from "./RewardsPanel";
import { useIdentity } from "@/lib/api/session";

/* The kit's mobile bar leads with Menu — the rail is a sheet on phones, so
   opening it has to be a first-class destination rather than a header icon. */
const matches = (tab: MobileTab, pathname: string) =>
  tab.exact.includes(pathname) || tab.prefixes.some((p) => pathname.startsWith(p));

export function MobileTabBar({ onMenu, menuOpen }: { onMenu: () => void; menuOpen: boolean }) {
  const pathname = usePathname();
  const { flags, nav } = useContent();
  const { identity, isSignedIn } = useIdentity();

  /* The Rewards tab is the bar's own, not the operator's: like the
     whitelabel's bottom navigation, a dedicated item is INSERTED once a
     session exists (its rewards:open action there, the sheet here), and any
     configured /rewards link is replaced by it — so the behaviour holds
     whatever the admin has done to the nav. Signed out, the configured link
     stays a link and nothing is inserted. */
  const REWARDS_TAB: MobileTab = { label: "Rewards", icon: "gift", href: "/rewards", prefixes: ["/rewards"], exact: [] };

  const configured = nav.mobile.filter((t) => flags.sportsEnabled || t.href !== "/sports");
  const rewardsIndex = configured.findIndex((t) => t.href === "/rewards");
  const tabs = isSignedIn
    ? (() => {
        const rest = configured.filter((t) => t.href !== "/rewards");
        const at = rewardsIndex === -1 ? Math.min(3, rest.length) : rewardsIndex;
        return [...rest.slice(0, at), REWARDS_TAB, ...rest.slice(at)];
      })()
    : configured;

  const [rewardsOpen, setRewardsOpen] = useState(false);
  const sheet = useModalChrome(rewardsOpen, () => setRewardsOpen(false));

  useEffect(() => {
    setRewardsOpen(false);
  }, [pathname]);

  return (
    <>
      <AnimatePresence>
        {rewardsOpen && (
          <div className="fixed inset-0 bottom-[calc(62px+var(--safe-bottom))] z-50 lg:hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setRewardsOpen(false)}
              className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
            />
            <motion.div
              ref={sheet}
              role="dialog"
              aria-modal="true"
              aria-label="Rewards"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.3, ease: EASE }}
              className="absolute inset-x-0 bottom-0 max-h-[75vh] overflow-y-auto rounded-t-xl border-t border-border bg-elevation-1"
            >
              <RewardsPanel onClose={() => setRewardsOpen(false)} />
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <nav aria-label="Mobile" className="fixed inset-x-0 bottom-0 z-40 flex h-[calc(62px+var(--safe-bottom))] items-center justify-center gap-[8px] border-t border-border bg-elevation-1 pb-[calc(8px+var(--safe-bottom))] pl-[calc(12px+env(safe-area-inset-left))] pr-[calc(12px+env(safe-area-inset-right))] lg:hidden">
        {tabs.map((t) => {
          const opensRewardsSheet = isSignedIn && t === REWARDS_TAB;
          const active = opensRewardsSheet
            ? rewardsOpen
            : t.href
              ? !menuOpen && !rewardsOpen && matches(t, pathname)
              : menuOpen;
          const inner = (
            <>
              <span className="h-[4px] w-[55px] shrink-0">
                {active && <motion.span layoutId="mobile-tab" transition={spring} className="block h-full w-full bg-brand" />}
              </span>
              <span className="flex shrink-0 items-center py-[8px]">
                {t.avatar ? (
                  <Avatar name={identity?.nickname ?? "Guest"} size={20} glow={active} />
                ) : (
                  <Icon name={t.icon} size={20} className={active ? "text-brand" : undefined} />
                )}
              </span>
              <span className="h-[14px] w-full truncate text-center">{t.label}</span>
            </>
          );

          const className =
            "relative flex h-[54px] flex-1 flex-col items-center justify-center text-[9px] font-medium leading-[1.5] text-fg transition-colors";

          if (opensRewardsSheet) {
            return (
              <button
                key={t.label}
                onClick={() => setRewardsOpen((o) => !o)}
                aria-expanded={rewardsOpen}
                className={className}
              >
                {inner}
              </button>
            );
          }

          return t.href ? (
            <Link key={t.label} href={t.href} aria-current={active ? "page" : undefined} className={className}>
              {inner}
            </Link>
          ) : (
            <button key={t.label} onClick={onMenu} aria-expanded={menuOpen} className={className}>
              {inner}
            </button>
          );
        })}
      </nav>
    </>
  );
}
